import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CardService } from '../services/cardService';
import type { MaxRateCard } from '../types/cardRecommendation';

type LocationState = { card?: MaxRateCard };

const formatInr = (amount: number | undefined | null) => {
  if (typeof amount !== 'number' || Number.isNaN(amount)) return '—';
  return `₹${amount.toLocaleString('en-IN')}`;
};

const RecommendedCardDetail: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const state = (location.state || {}) as LocationState;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [card, setCard] = useState<MaxRateCard | null>(state.card ?? null);

  useEffect(() => {
    document.title = 'Recommended Card';
  }, []);

  useEffect(() => {
    if (card) return;

    let isMounted = true;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await CardService.getMaxRateCard();
        if (!isMounted) return;
        setCard(res.data.card);
      } catch (e) {
        if (!isMounted) return;
        setError(e instanceof Error ? e.message : 'Failed to load recommended card');
      } finally {
        if (!isMounted) return;
        setLoading(false);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [card]);

  const topCategories = useMemo(() => (card?.categories || []).slice(0, 3), [card]);
  const bestRate = useMemo(() => (card?.earnRates?.other && card.earnRates.other.length > 0 ? card.earnRates.other[0] : null), [card]);

  if (loading && !card) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        <p className="mt-2 text-gray-500">Loading recommended card...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Card Detail</h1>
          <p className="mt-1 text-sm text-gray-500">Recommended Card</p>
        </div>
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
        >
          Back
        </button>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-4 text-sm">{error}</div>}

      {!card ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="text-sm text-gray-700">No card data available.</div>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="text-xs uppercase tracking-widest text-gray-500">{String(card.bankName || '—')}</div>
            <div className="mt-1 text-2xl font-bold text-gray-900">{String(card.name || '—')}</div>

            <div className="mt-3 flex flex-wrap gap-2">
              {topCategories.map((c) => (
                <span key={c} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                  {c}
                </span>
              ))}
              {topCategories.length === 0 && (
                <span className="text-sm text-gray-500">No categories</span>
              )}
            </div>

            <div className="mt-5 bg-blue-50 border border-blue-200 rounded-md p-4">
              <div className="text-sm font-semibold text-blue-900">Maximum Reward Rate</div>
              <div className="mt-1 text-2xl font-bold text-blue-900">{(Number(card.maxRate) * 100).toFixed(2)}% per ₹1</div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-sm font-semibold text-gray-900">Earning Structure</h2>

            <div className="mt-3">
              <div className="text-xs uppercase tracking-wider text-gray-500">Base rate</div>
              <div className="mt-1 text-sm text-gray-900">
                {card.earnRates?.default
                  ? `${card.earnRates.default.per} ${card.earnRates.default.unit} / ₹${card.earnRates.default.perAmount}`
                  : '—'}
              </div>
              {card.earnRates?.default?.note && <div className="mt-1 text-sm text-gray-600">{card.earnRates.default.note}</div>}
              {typeof card.earnRates?.default?.effective_rate_per_inr === 'number' && (
                <div className="mt-1 text-sm text-gray-700">
                  Effective rate: {card.earnRates.default.effective_rate_per_inr.toFixed(5)} {card.earnRates.default.unit} per ₹1
                </div>
              )}
            </div>

            <div className="mt-5">
              <div className="text-xs uppercase tracking-wider text-gray-500">Other rates</div>
              <div className="mt-2 space-y-3">
                {(card.earnRates?.other || []).map((r) => (
                  <div key={`${r.category}-${r.per}-${r.perAmount}`} className="border border-gray-200 rounded-md p-3">
                    <div className="text-sm font-medium text-gray-900">{r.category}</div>
                    <div className="mt-1 text-sm text-gray-800">
                      {r.per} {r.unit} / ₹{r.perAmount}
                    </div>
                    {typeof r.cap_per_month_rp === 'number' && (
                      <div className="mt-1 text-sm text-gray-700">Monthly cap: {r.cap_per_month_rp.toLocaleString('en-IN')} {r.unit}</div>
                    )}
                    {typeof r.effective_rate_per_inr === 'number' && (
                      <div className="mt-1 text-sm text-gray-700">Effective rate: {r.effective_rate_per_inr.toFixed(5)} {r.unit} per ₹1</div>
                    )}
                    {r.note && <div className="mt-1 text-sm text-gray-600">{r.note}</div>}
                  </div>
                ))}

                {(card.earnRates?.other || []).length === 0 && (
                  <div className="text-sm text-gray-500">No category-specific rates.</div>
                )}
              </div>
            </div>

            {bestRate && (
              <div className="mt-5 bg-gray-50 border border-gray-200 rounded-md p-3">
                <div className="text-sm font-semibold text-gray-900">Best rate</div>
                <div className="mt-1 text-sm text-gray-800">
                  {bestRate.category} with {bestRate.per} {bestRate.unit} per ₹{bestRate.perAmount}
                </div>
              </div>
            )}
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-sm font-semibold text-gray-900">Annual Fees</h2>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <div className="text-xs uppercase tracking-wider text-gray-500">Primary card</div>
                <div className="mt-1 text-sm font-medium text-gray-900">{formatInr(card.annualFees?.primary)}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-gray-500">Add-on card</div>
                <div className="mt-1 text-sm font-medium text-gray-900">
                  {typeof card.annualFees?.addOn === 'number' ? (card.annualFees.addOn === 0 ? 'Free' : formatInr(card.annualFees.addOn)) : '—'}
                </div>
              </div>
            </div>

            {card.annualFees?.waiver && (
              <div className="mt-4 border border-gray-200 rounded-md p-3">
                <div className="text-sm font-semibold text-gray-900">Fee waiver</div>
                <div className="mt-1 text-sm text-gray-800">Spend threshold: {formatInr(card.annualFees.waiver.spendThreshold)}</div>
                {card.annualFees.waiver.condition && <div className="mt-1 text-sm text-gray-700">{card.annualFees.waiver.condition}</div>}
              </div>
            )}
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-sm font-semibold text-gray-900">Milestone Benefits</h2>
            <div className="mt-3 space-y-3">
              {(card.milestones || []).map((m, idx) => (
                <div key={`${m.spendThreshold ?? 'm'}-${idx}`} className="border border-gray-200 rounded-md p-3">
                  <div className="text-sm text-gray-900">Spend threshold: {formatInr(m.spendThreshold)}</div>
                  {m.period && <div className="mt-1 text-sm text-gray-700">Period: {m.period}</div>}
                  {(m.benefit || m.description) && (
                    <div className="mt-1 text-sm text-gray-700">{m.benefit || m.description}</div>
                  )}
                </div>
              ))}
              {(card.milestones || []).length === 0 && <div className="text-sm text-gray-500">No milestones available.</div>}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-sm font-semibold text-gray-900">Reference Info</h2>
            <div className="mt-3 grid grid-cols-1 gap-3">
              <div>
                <div className="text-xs uppercase tracking-wider text-gray-500">Source</div>
                <div className="mt-1 text-sm text-gray-800">{card.reference?.source || '—'}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-gray-500">Disclaimer</div>
                <div className="mt-1 text-sm text-gray-800">{card.reference?.disclaimer || '—'}</div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default RecommendedCardDetail;
